import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box, Button, TextField, Typography, Paper, Divider,
  Autocomplete, MenuItem, Stack, ListItem, ListItemText,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, Chip, FormControl, FormLabel,
  RadioGroup, FormControlLabel, Radio, Tab, Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HealthIcon from '@mui/icons-material/HealthAndSafety';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import StarterKit from '@tiptap/starter-kit';
import {
  RichTextEditor,
  type RichTextEditorRef,
  MenuControlsContainer,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
  MenuDivider,
  MenuButtonUndo,
  MenuButtonRedo,
} from 'mui-tiptap';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import {
  addEpic, updateEpic, deleteEpic, removeCategoryFromEpics,
  computeEpicHealthItems, getEpicHealthPercent, toggleEpicHealthAcknowledge,
  addEpicComment, updateEpicComment, deleteEpicComment,
  toggleEpicCommentReaction, addEpicCommentReply,
  updateEpicCommentReply, deleteEpicCommentReply,
} from '../../store/slices/epicsSlice';
import type { HistoryChange, HistoryEntry, ReactionEmoji } from '../../store/slices/sharedTypes';
import { Story, StoryType, EngineeringSubtype } from '../../store/slices/storiesSlice';
import {
  getHealthColor,
  getHealthTierLabel,
} from '../Backlog/Backlog';
import {
  PARENT_STATES, PARENT_STATE_COLORS, ParentState,
} from '../../store/slices/stateManagementSlice';
import { addCategory, updateCategory, removeCategory } from '../../store/slices/categoriesSlice';
import { categoryColors, darkCategoryColors } from '../../theme';
import ModalActionFooter from '../../components/ModalActionFooter/ModalActionFooter';
import NumberInput from '../../components/NumberInput/NumberInput';
import JsonViewer from '../../components/JsonViewer/JsonViewer';
import { purple } from '@mui/material/colors';
/** Placeholder identity used until real auth is wired up. */
const CURRENT_USER_ID = 'current-user';
const CURRENT_USER_NAME = 'You';

const REACTION_EMOJIS: ReactionEmoji[] = ['👍', '🔥', '🎉'];

/** Format an ISO date string as a friendly relative or absolute label. */
const formatDate = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Derive user initials for Avatar fallback. */
const getInitials = (name: string): string =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);


const EpicForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = (location.state as { from?: string } | null)?.from ?? (location.pathname.startsWith('/backlog') ? '/backlog' : '/epic-planner');
  const { epicId } = useParams<{ epicId: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const pods = useAppSelector((state) => state.pods.pods);
  const categories = useAppSelector((state) => state.categories.categories);
  const workstreams = useAppSelector((state) => state.workstreams.workstreams);
  const existingEpic = useAppSelector((state) =>
    state.epics.epics.find((e) => e.id === epicId)
  );
  const themeMode = useAppSelector((state) => state.theme.mode);
  const isDark = themeMode === 'dark';
  const colorPalette = isDark ? darkCategoryColors : categoryColors;
  const epicWeights = useAppSelector((state) => state.healthConfig.epicWeights);
  const storyWeights = useAppSelector((state) => state.healthConfig.storyWeights);
  const epicStatuses = useAppSelector((state) => state.stateManagement.epicStatuses);
  const developerMode = useAppSelector((state) => state.developerMode.developerMode);

  const defaultWorkstreamId = searchParams.get('workstreamId') || '';

  const allEpics = useAppSelector((state) => state.epics.epics);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [podId, setPodId] = useState('');
  const [workstreamId, setWorkstreamId] = useState(defaultWorkstreamId);
  const [parentState, setParentState] = useState<ParentState | ''>('');
  const [status, setStatus] = useState('');

  // Add Category modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState(colorPalette[0]);

  // Edit Category modal state
  const [editCatModalOpen, setEditCatModalOpen] = useState(false);
  const [editCatId, setEditCatId] = useState<string | null>(null);
  const [editCatName, setEditCatName] = useState('');
  const [editCatColor, setEditCatColor] = useState('');

  const [capacity, setCapacity] = useState({
    Engineering: 0,
    practices: {} as Record<string, number>,
  });

  // Story Breakdown state
  const [stories, setStories] = useState<Story[]>([]);
  const [storyPointOverrides, setStoryPointOverrides] = useState<Partial<Record<StoryType, number>>>({});

  // Story modal state
  const [storyModalOpen, setStoryModalOpen] = useState(false);
  const [editingStory, setEditingStory] = useState<Story | null>(null);
  const [storyTitle, setStoryTitle] = useState('');
  const [storyDesc, setStoryDesc] = useState('');
  const [storyType, setStoryType] = useState<StoryType>('Engineering');
  const [storyEngSubtype, setStoryEngSubtype] = useState<EngineeringSubtype>('Frontend');
  const [storyPoints, setStoryPoints] = useState(0);
  const [storyEpicId, setStoryEpicId] = useState<string>('');
  const selectedStoryEpic = useMemo(
    () => allEpics.find((e) => e.id === storyEpicId) ?? null,
    [allEpics, storyEpicId],
  );

  // Right-panel tab state
  const [activeTab, setActiveTab] = useState(0);

  // JSON config viewer state
  const [jsonViewerOpen, setJsonViewerOpen] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);

  // Comment editor state
  const [newCommentContent, setNewCommentContent] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [replyingToCommentId, setReplyingToCommentId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingReplyContent, setEditingReplyContent] = useState('');

  const commentEditorRef = useRef<RichTextEditorRef>(null);
  const editCommentEditorRef = useRef<RichTextEditorRef>(null);
  const commentExtensions = useMemo(
    () => [StarterKit.configure({ link: { openOnClick: false } }), LinkBubbleMenuHandler],
    [],
  );

  // Track the last epic ID we initialized local state from.
  // This ensures we only reset local form state when navigating to a *different*
  // epic — not every time the same epic changes in Redux (e.g. health acknowledgments).
  const initializedEpicId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (existingEpic && initializedEpicId.current !== existingEpic.id) {
      initializedEpicId.current = existingEpic.id;
      setTitle(existingEpic.title);
      setDescription(existingEpic.description);
      setCategoryId(existingEpic.categoryId);
      setPodId(existingEpic.podId ?? '');
      setWorkstreamId(existingEpic.workstreamId ?? '');
      setCapacity({
        Engineering: existingEpic.capacity.Engineering,
        practices: existingEpic.capacity.practices || {},
      });
      setStories(existingEpic.stories ?? []);
      setStoryPointOverrides(existingEpic.storyPointOverrides ?? {});
      setParentState(existingEpic.parentState ?? '');
      setStatus(existingEpic.status ?? '');
    } else if (!existingEpic) {
      // Reset so that if existingEpic reappears (same ID) we re-initialize.
      initializedEpicId.current = undefined;
    }
  }, [existingEpic]);

  const STORY_TYPE_COLOR: Record<StoryType, 'primary' | 'secondary' | 'success'> = {
    Engineering: 'success',
  };

  const computedStoryTotals: Record<StoryType, number> = {
    Engineering: stories.filter((s) => s.type === 'Engineering').reduce((sum, s) => sum + s.points, 0),
  };

  const openStoryModal = (story?: Story) => {
    if (story) {
      setEditingStory(story);
      setStoryTitle(story.title);
      setStoryDesc(story.description);
      setStoryType(story.type);
      setStoryEngSubtype(story.engineeringSubtype ?? 'Frontend');
      setStoryPoints(story.points);
      // when editing a story inside this epic, preselect this epic
      setStoryEpicId(existingEpic?.id ?? '');
    } else {
      setEditingStory(null);
      setStoryTitle('');
      setStoryDesc('');
      setStoryType('Engineering');
      setStoryEngSubtype('Frontend');
      setStoryPoints(0);
      // default new story epic selection to none
      setStoryEpicId('');
    }
    setStoryModalOpen(true);
  };

  const handleSaveStory = () => {
    if (!storyTitle.trim()) return;
    const data: Story = {
      id: editingStory?.id ?? uuidv4(),
      title: storyTitle.trim(),
      description: storyDesc,
      type: storyType,
      ...(storyType === 'Engineering' && { engineeringSubtype: storyEngSubtype }),
      points: storyPoints,
    };
    setStories((prev) =>
      editingStory ? prev.map((s) => (s.id === editingStory.id ? data : s)) : [...prev, data]
    );
    setStoryModalOpen(false);
  };

  const handleDeleteStory = (id: string) => {
    setStories((prev) => prev.filter((s) => s.id !== id));
    setStoryModalOpen(false);
  };

  const handleDragEndStories = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(stories);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setStories(items);
  };

  const handleResetStoryOverride = (type: StoryType) => {
    setStoryPointOverrides((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
  };

  const handleSetStoryPointOverride = (type: StoryType, value: number) => {
    setStoryPointOverrides((prev) => ({ ...prev, [type]: Math.max(0, value) }));
  };

  // Map any stored color to the equivalent in the current mode's palette (by index).
  // Light and dark palettes correspond 1-to-1 by index.
  const resolveDisplayColor = React.useCallback((storedColor: string): string => {
    const lightIdx = categoryColors.indexOf(storedColor);
    if (lightIdx !== -1) return colorPalette[lightIdx];
    const darkIdx = darkCategoryColors.indexOf(storedColor);
    if (darkIdx !== -1) return colorPalette[darkIdx];
    return storedColor;
  }, [colorPalette]);

  // Add Category modal handlers
  const handleOpenAddModal = () => {
    setNewCategoryName('');
    setNewCategoryColor(colorPalette[categories.length % colorPalette.length]);
    setAddModalOpen(true);
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    dispatch(addCategory({ name: newCategoryName.trim(), color: newCategoryColor }));
    setAddModalOpen(false);
  };

  // Edit Category modal handlers
  const openEditCatModal = (cat: { id: string; name: string; color: string }) => {
    setEditCatId(cat.id);
    setEditCatName(cat.name);
    setEditCatColor(resolveDisplayColor(cat.color));
    setEditCatModalOpen(true);
  };

  const handleSaveCatEdit = () => {
    if (editCatId && editCatName.trim()) {
      dispatch(updateCategory({ id: editCatId, name: editCatName.trim(), color: editCatColor }));
    }
    setEditCatModalOpen(false);
  };

  const handleConfirmDeleteCat = () => {
    if (!editCatId) return;
    dispatch(removeCategory(editCatId));
    dispatch(removeCategoryFromEpics(editCatId));
    if (categoryId === editCatId) setCategoryId(null);
    setEditCatModalOpen(false);
  };

  const handleSave = () => {
    if (!title.trim() || !workstreamId) return;

    // Compute history changes for existing epics
    const changes: HistoryChange[] = [];
    if (existingEpic) {
      if (existingEpic.title !== title.trim()) {
        changes.push({ field: 'Title', from: existingEpic.title, to: title.trim() });
      }
      if ((existingEpic.description ?? '') !== description) {
        changes.push({ field: 'Description', from: existingEpic.description || '(empty)', to: description || '(empty)' });
      }
      if ((existingEpic.podId ?? '') !== podId) {
        const oldPod = pods.find((p) => p.id === existingEpic.podId)?.name ?? existingEpic.podId ?? '(none)';
        const newPod = pods.find((p) => p.id === podId)?.name ?? (podId || '(none)');
        changes.push({ field: 'Pod', from: oldPod, to: newPod });
      }
      if ((existingEpic.workstreamId ?? '') !== workstreamId) {
        const oldWs = existingEpic.workstreamId
          ? (workstreams.find((w) => w.id === existingEpic.workstreamId)?.name ?? '(none)')
          : '(none)';
        const newWs = workstreamId
          ? (workstreams.find((w) => w.id === workstreamId)?.name ?? '(none)')
          : '(none)';
        changes.push({ field: 'Workstream', from: oldWs, to: newWs });
      }
      if (existingEpic.categoryId !== categoryId) {
        const oldCat = categories.find((c) => c.id === existingEpic.categoryId)?.name ?? '(none)';
        const newCat = categories.find((c) => c.id === categoryId)?.name ?? '(none)';
        changes.push({ field: 'Category', from: oldCat, to: newCat });
      }
      if ((existingEpic.parentState ?? '') !== parentState) {
        changes.push({ field: 'State', from: existingEpic.parentState ?? '(none)', to: parentState || '(none)' });
      }
      if ((existingEpic.status ?? '') !== status) {
        changes.push({ field: 'Status', from: existingEpic.status ?? '(none)', to: status || '(none)' });
      }
    }
    const newHistoryEntries: HistoryEntry[] = changes.length > 0 && existingEpic
      ? [{ id: uuidv4(), timestamp: new Date().toISOString(), changedBy: CURRENT_USER_NAME, changes }]
      : [];

    const epicData = {
      title,
      description,
      categoryId,
      podId: podId || null,
      workstreamId: workstreamId || null,
      priority: existingEpic?.priority ?? 999,
      capacity: {
        Engineering: capacity.Engineering,
        practices: capacity.practices,
      },
      stories,
      storyPointOverrides,
      parentState: parentState || undefined,
      status: status || undefined,
      comments: existingEpic?.comments ?? [],
      history: [...newHistoryEntries, ...(existingEpic?.history ?? [])],
    };
    if (existingEpic) {
      dispatch(updateEpic({ ...existingEpic, ...epicData }));
    } else {
      dispatch(addEpic(epicData));
    }
    navigate(backPath);
  };

  const handleDelete = () => {
    if (existingEpic) {
      dispatch(deleteEpic(existingEpic.id));
      navigate(backPath);
    }
  };

  // ── Comment handlers ────────────────────────────────────────────────────

  const epicComments = existingEpic?.comments ?? [];

  const handlePostEpicComment = () => {
    if (!existingEpic || !newCommentContent.trim()) return;
    dispatch(addEpicComment({
      epicId: existingEpic.id,
      comment: { authorId: CURRENT_USER_ID, authorName: CURRENT_USER_NAME, content: newCommentContent },
    }));
    setNewCommentContent('');
    commentEditorRef.current?.editor?.commands.clearContent();
  };

  const handleSaveEditEpicComment = (commentId: string) => {
    if (!existingEpic || !editingCommentContent.trim()) return;
    dispatch(updateEpicComment({ epicId: existingEpic.id, commentId, content: editingCommentContent }));
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const handleDeleteEpicComment = (commentId: string) => {
    if (!existingEpic) return;
    dispatch(deleteEpicComment({ epicId: existingEpic.id, commentId }));
  };

  const handleToggleEpicReaction = (commentId: string, emoji: ReactionEmoji) => {
    if (!existingEpic) return;
    dispatch(toggleEpicCommentReaction({ epicId: existingEpic.id, commentId, emoji, userId: CURRENT_USER_ID }));
  };

  const handlePostEpicReply = (commentId: string) => {
    if (!existingEpic || !replyContent.trim()) return;
    dispatch(addEpicCommentReply({
      epicId: existingEpic.id,
      commentId,
      reply: { authorId: CURRENT_USER_ID, authorName: CURRENT_USER_NAME, content: replyContent.trim() },
    }));
    setReplyContent('');
    setReplyingToCommentId(null);
  };

  const handleSaveEditEpicReply = (commentId: string, replyId: string) => {
    if (!existingEpic || !editingReplyContent.trim()) return;
    dispatch(updateEpicCommentReply({ epicId: existingEpic.id, commentId, replyId, content: editingReplyContent.trim() }));
    setEditingReplyId(null);
    setEditingReplyContent('');
  };

  const handleDeleteEpicReply = (commentId: string, replyId: string) => {
    if (!existingEpic) return;
    dispatch(deleteEpicCommentReply({ epicId: existingEpic.id, commentId, replyId }));
  };

  // ── Health ────────────────────────────────────────────────────────────────

  const epicHealthItems = existingEpic ? computeEpicHealthItems({
    ...existingEpic,
    description,
    stories,
    storyPointOverrides,
  }, epicWeights) : [];

  /** Health percentage (0-100) matching the score shown in the Backlog view. */
  const epicHealthPercent = useMemo((): number => {
    if (!existingEpic) return 100;
    return getEpicHealthPercent(
      { ...existingEpic, description, stories, storyPointOverrides },
      epicWeights,
      storyWeights,
    );
  }, [existingEpic, description, stories, storyPointOverrides, epicWeights, storyWeights]);

  /** Interpolated RGB colour matching the Backlog's health chip colour. */
  const epicHealthColor = getHealthColor(epicHealthPercent);
  /** Human-readable tier label (e.g. "Healthy", "Attention") matching the Backlog. */
  const epicHealthLabel = getHealthTierLabel(epicHealthPercent);

  const showHealth = existingEpic !== null;

  /**
   * True when this is an existing closed epic and the local state selection is
   * still 'Closed'. All fields except the State selector are disabled in this
   * case — the user must change the state away from 'Closed' to unlock editing.
   */
  const isFormLocked = !!(existingEpic && parentState === 'Closed');

  return (
    <>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {/* ── Left Paper: Main Form ── */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon fontSize="large" sx={{ color: purple[500] }} />
              <Typography variant="h5" fontWeight={600}>
                {existingEpic ? title : 'New Epic'}
              </Typography>
              {showHealth && (
                <Tooltip title={`Click to view health details · ${epicHealthLabel}`} arrow>
                  <Chip
                    icon={<HealthIcon />}
                    label={`Health: ${epicHealthPercent}%`}
                    size="medium"
                    onClick={() => setActiveTab(2)}
                    sx={{ ml: 'auto', cursor: 'pointer', fontWeight: 600, bgcolor: epicHealthColor, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
                  />
                </Tooltip>
              )}
              {!showHealth && developerMode && <Box sx={{ ml: 'auto' }} />}
              {developerMode && existingEpic && (
                <Tooltip title="View configuration (JSON)" arrow>
                  <IconButton
                    size="small"
                    onClick={() => { setJsonCopied(false); setJsonViewerOpen(true); }}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'warning.main' } }}
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Divider sx={{ mt: 1, mb: 3 }} />
            {isFormLocked && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'action.disabledBackground',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1.5,
                  mb: 2,
                }}
              >
                <LockOutlinedIcon sx={{ color: 'text.disabled', fontSize: 18, flexShrink: 0 }} />
                <Typography variant="body2" color="text.disabled">
                  This epic is closed and cannot be edited. Change the state in the panel on the right to reopen it.
                </Typography>
              </Box>
            )}
            <Stack spacing={3}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                disabled={isFormLocked}
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                disabled={isFormLocked}
              />

              {/* Story Breakdown */}
              <Box sx={{ gap: 1.5, mb: 1, backgroundColor: 'background.paper', p: 1, borderRadius: 1, borderColor: 'divider', borderWidth: 1, borderStyle: 'solid' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" color="text.secondary">Story Breakdown</Typography>
                  {!isFormLocked && (
                    <Button size="small" startIcon={<AddIcon />} onClick={() => openStoryModal()}>
                      Add Story
                    </Button>
                  )}
                </Box>
                <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                  {stories.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No stories yet. Click 'Add Story' to break down this epic."
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                  <DragDropContext onDragEnd={handleDragEndStories}>
                    <Droppable droppableId="stories">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          {stories.map((story, idx) => {
                            const isLast = idx === stories.length - 1;
                            const typeColor = STORY_TYPE_COLOR[story.type];

                            return (
                              <Draggable key={story.id} draggableId={story.id} index={idx}>
                                {(dragProvided) => (
                                  <ListItem
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    sx={{
                                      borderBottom: isLast ? 'none' : '1px solid',
                                      borderColor: 'divider',
                                      gap: 1,
                                      pr: 0.5,
                                      '&:hover': {
                                        bgcolor: 'action.hover',
                                        '& .story-edit-btn': { opacity: 1 },
                                      },
                                      '&:focus-within': { '& .story-edit-btn': { opacity: 1 } },
                                    }}
                                  >
                                    <Box
                                      {...dragProvided.dragHandleProps}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      sx={{ mr: 0.5, cursor: 'grab', color: 'text.secondary', display: 'flex', alignItems: 'center' }}
                                    >
                                      <DragIndicatorIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                        <Chip
                                          label={story.engineeringSubtype ? `${story.type} · ${story.engineeringSubtype}` : story.type}
                                          size="small"
                                          color={typeColor}
                                          variant="outlined"
                                        />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{story.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{story.points} pts</Typography>
                                      </Box>
                                    </Box>
                                    <Tooltip title="Edit story">
                                      <IconButton
                                        className="story-edit-btn"
                                        size="small"
                                        onClick={(e) => { e.currentTarget.blur(); openStoryModal(story); }}
                                        sx={{ opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </ListItem>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Paper>
              </Box>


            </Stack>

            <Box sx={{ mt: 4 }}>
              <ModalActionFooter
                onSave={handleSave}
                saveLabel={existingEpic ? 'Save' : 'Create'}
                saveDisabled={!title.trim() || !workstreamId || isFormLocked}
                onCancel={() => navigate(backPath)}
                onDelete={existingEpic ? handleDelete : undefined}
                deleteLabel="Delete Epic"
                deleteConfirmMessage="Are you sure you want to delete this epic? This action cannot be undone."
              />
            </Box>
          </Paper>

          {/* ── Right Paper: Controls ── */}
          <Paper elevation={2} sx={{ overflow: 'hidden', position: { md: 'sticky' }, top: { md: 80 }, height: '100%' }}>
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1" color="text.secondary">Epic Controls</Typography>

                {/* State */}
                <TextField
                  select
                  label="State"
                  value={parentState}
                  onChange={(e) => {
                    setParentState(e.target.value as ParentState | '');
                    setStatus('');
                  }}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {PARENT_STATES.map((ps) => (
                    <MenuItem key={ps} value={ps}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PARENT_STATE_COLORS[ps], flexShrink: 0 }} />
                        {ps}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
                {/* Status */}
                {parentState && epicStatuses[parentState].length > 0 && (
                  <TextField
                    select
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {epicStatuses[parentState].map((st) => (
                      <MenuItem key={st.id} value={st.title}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: st.color, flexShrink: 0 }} />
                          {st.title}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {/* Workstream (primary required field) */}
                <TextField
                  select
                  label="Workstream"
                  value={workstreamId}
                  onChange={(e) => setWorkstreamId(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  disabled={isFormLocked}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {workstreams.map((w) => (
                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                  ))}
                </TextField>

                {/* Assigned Pod (optional) */}
                <TextField
                  select
                  label="Assigned Pod"
                  value={podId}
                  onChange={(e) => setPodId(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={isFormLocked}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {pods.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </TextField>

                {/* Category */}
                <Box sx={{ pointerEvents: isFormLocked ? 'none' : 'auto', opacity: isFormLocked ? 0.5 : 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" color="text.secondary">Category</Typography>
                    {!isFormLocked && (
                      <Button size="small" startIcon={<AddIcon />} onClick={handleOpenAddModal}>
                        Add
                      </Button>
                    )}
                  </Box>
                  <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                    {/* None option */}
                    <ListItem
                      tabIndex={0}
                      sx={{
                        borderBottom: categories.length > 0 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        bgcolor: categoryId === null ? 'action.selected' : 'transparent',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
                      }}
                      onClick={() => setCategoryId(null)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCategoryId(null); } }}
                    >
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#9e9e9e', mr: 2, flexShrink: 0 }} />
                      <ListItemText primary="None" primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>

                    {categories.map((cat, index) => {
                      const isSelected = categoryId === cat.id;
                      const isLast = index === categories.length - 1;
                      return (
                        <ListItem
                          key={cat.id}
                          tabIndex={0}
                          sx={{
                            borderBottom: isLast ? 'none' : '1px solid',
                            borderColor: 'divider',
                            bgcolor: isSelected ? 'action.selected' : 'transparent',
                            cursor: 'pointer',
                            pr: 0.5,
                            '&:hover': {
                              bgcolor: isSelected ? 'action.selected' : 'action.hover',
                              '& .cat-edit-btn': { opacity: 1 },
                            },
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
                          }}
                          onClick={() => setCategoryId(cat.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCategoryId(cat.id); } }}
                        >
                          <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: resolveDisplayColor(cat.color), mr: 2, flexShrink: 0 }} />
                          <ListItemText primary={cat.name} primaryTypographyProps={{ variant: 'body2' }} sx={{ flexGrow: 1 }} />
                          <Tooltip title="Edit category">
                            <IconButton
                              className="cat-edit-btn"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); openEditCatModal(cat); }}
                              onKeyDown={(e) => e.stopPropagation()}
                              sx={{ opacity: 0, transition: 'opacity 0.15s' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItem>
                      );
                    })}

                    {categories.length === 0 && (
                      <ListItem>
                        <ListItemText
                          primary="No categories yet. Click 'Add' to create one."
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    )}
                  </Paper>
                </Box>
                {/* Story Point Totals */}
                <Box sx={{ gap: 1.5, mb: 1, backgroundColor: 'background.paper', p: 1, borderRadius: 1, borderColor: 'divider', borderWidth: 1, borderStyle: 'solid', pointerEvents: isFormLocked ? 'none' : 'auto', opacity: isFormLocked ? 0.5 : 1 }}>
                  <Stack spacing={1}>
                    {(['Engineering'] as const).map((type) => {
                      const computed = computedStoryTotals[type];
                      const hasOverride = type in storyPointOverrides;
                      const displayValue = hasOverride ? (storyPointOverrides[type] ?? 0) : computed;
                      const isCustom = hasOverride && storyPointOverrides[type] !== computed;
                      return (
                        <Box key={type}>
                          <Typography variant="subtitle2" gutterBottom>{type} Story Point Total</Typography>
                          <Box
                            sx={{
                              border: '1px solid', borderColor: isCustom ? 'warning.main' : 'divider',
                              borderRadius: 1, p: 1.5,
                              marginTop: 1.5,
                              marginBottom: 2,
                            }}
                          >
                            <NumberInput
                              value={displayValue}
                              onChange={(val) => handleSetStoryPointOverride(type, val)}
                              min={0}
                              max={200}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                              <Typography variant="caption" color={isCustom ? 'warning.main' : 'text.secondary'}>
                                {hasOverride ? `Computed: ${computed} pts` : `From stories: ${computed} pts`}
                              </Typography>
                              {isCustom && (
                                <Chip label="Custom" size="small" color="warning" variant="outlined" />
                              )}
                              {hasOverride && (
                                <Tooltip title={`Reset to computed value (${computed} pts)`}>
                                  <Button size="small" onClick={() => handleResetStoryOverride(type)}>
                                    Reset
                                  </Button>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>



              </Stack>
            </Box>
          </Paper>
        </Box>

        {/* ── Bottom Paper: Tabbed Panel (History, Health, Comments) ── */}
        <Paper elevation={2} sx={{ mt: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={
                <Badge badgeContent={epicComments.length > 0 ? epicComments.length : undefined} color="primary" max={99}>
                  <ChatBubbleOutlineIcon fontSize="small" />
                </Badge>
              }
              aria-label="Comments"
              title="Comments"
              sx={{ minWidth: 0, py: 1.5 }}
            />
            <Tab
              icon={<HistoryIcon fontSize="small" />}
              aria-label="History"
              title="Change History"
              sx={{ minWidth: 0, py: 1.5 }}
            />
            <Tab
              icon={
                <Tooltip title={showHealth ? `Health: ${epicHealthPercent}% · ${epicHealthLabel}` : 'Health'}>
                  <HealthIcon
                    fontSize="small"
                    sx={{ color: showHealth ? epicHealthColor : 'inherit' }}
                  />
                </Tooltip>
              }
              aria-label="Health"
              title="Health"
              sx={{ minWidth: 0, py: 1.5 }}
            />
          </Tabs>

          {/* ── Tab 1: History ── */}
          {activeTab === 1 && (
            <Box sx={{ p: 2, maxHeight: 520, overflowY: 'auto' }}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1.5 }}>
                Change History
              </Typography>
              {(!existingEpic?.history || existingEpic.history.length === 0) ? (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <HistoryIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">No history yet. Changes are recorded each time you save.</Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {existingEpic.history.map((entry) => (
                    <Box key={entry.id} sx={{ display: 'flex', gap: 1.5, mb: 1, backgroundColor: 'background.paper', p: 2, borderRadius: 1, borderColor: 'divider', borderWidth: 1, borderStyle: 'solid' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(entry.timestamp)} · {entry.changedBy}
                        </Typography>
                      </Box>
                      <Stack spacing={0.5} sx={{ pl: 3 }}>
                        {entry.changes.map((change, idx) => (
                          <Box key={idx} sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" fontWeight={600}>{change.field}:</Typography>
                            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'error.main' }}>{change.from}</Typography>
                            <Typography variant="caption" color="text.secondary">→</Typography>
                            <Typography variant="caption" sx={{ color: 'success.dark' }}>{change.to}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )
          }

          {/* ── Tab 2: Health ── */}
          {
            activeTab === 2 && (
              <Box sx={{ p: 2, maxHeight: 520, overflowY: 'auto' }}>
                {!showHealth ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <HealthIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">
                      Save the epic first to see health information.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <HealthIcon
                        fontSize="small"
                        sx={{ color: epicHealthColor }}
                      />
                      <Typography variant="subtitle1" color="text.secondary">Epic Health</Typography>
                      <Chip
                        label={`${epicHealthPercent}%`}
                        size="small"
                        sx={{ ml: 'auto', fontWeight: 600, bgcolor: epicHealthColor, color: '#fff' }}
                      />
                    </Box>
                    {epicHealthItems.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 32, opacity: 0.5, mb: 0.5 }} />
                        <Typography variant="body2">No health issues detected.</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={1}>
                        {epicHealthItems.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: item.acknowledged ? 'success.main' : 'warning.main',
                              bgcolor: item.acknowledged ? 'success.light' : 'warning.light',
                              opacity: item.acknowledged ? 0.75 : 1,
                            }}
                          >
                            <Tooltip
                              title={item.acknowledged ? 'Click to unacknowledge this health issue' : 'Click to acknowledge this health issue'}
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  dispatch(toggleEpicHealthAcknowledge({ epicId: existingEpic!.id, healthItemId: item.id }))
                                }
                                sx={{ color: item.acknowledged ? 'success.dark' : 'warning.dark' }}
                                aria-label={item.acknowledged ? `Unacknowledge: ${item.reason}` : `Acknowledge: ${item.reason}`}
                              >
                                {item.acknowledged ? (
                                  <CheckCircleOutlineIcon fontSize="small" />
                                ) : (
                                  <WarningAmberIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Typography variant="body2" sx={{ flexGrow: 1, color: item.acknowledged ? 'success.dark' : 'warning.dark' }}>
                              {item.reason}
                            </Typography>
                            <Chip
                              label={`+${item.score}`}
                              size="small"
                              color={item.acknowledged ? 'success' : 'warning'}
                              variant={item.acknowledged ? 'outlined' : 'filled'}
                              sx={{ fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            )
          }

          {/* ── Tab 0: Comments ── */}
          {
            activeTab === 0 && (
              <Box sx={{ p: 2, overflowY: 'auto' }}>
                {existingEpic ? (
                  <>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                      Add a comment
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <RichTextEditor
                        sx={{ '& .MuiTiptap-RichTextField-content': { minHeight: 80 } }}
                        ref={commentEditorRef}
                        extensions={commentExtensions}
                        content={newCommentContent}
                        onUpdate={({ editor }) => setNewCommentContent(editor.getHTML())}
                        renderControls={() => (
                          <MenuControlsContainer>
                            <MenuButtonBold />
                            <MenuButtonItalic />
                            <MenuButtonUnderline />
                            <MenuDivider />
                            <MenuButtonBulletedList />
                            <MenuButtonOrderedList />
                            <MenuDivider />
                            <MenuButtonUndo />
                            <MenuButtonRedo />
                          </MenuControlsContainer>
                        )}
                        RichTextFieldProps={{ variant: 'outlined' }}
                      >
                        {() => <LinkBubbleMenu />}
                      </RichTextEditor>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SendIcon fontSize="small" />}
                        onClick={handlePostEpicComment}
                        disabled={!newCommentContent.trim() || newCommentContent === '<p></p>'}
                      >
                        Post
                      </Button>
                    </Box>

                    {epicComments.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
                        <Typography variant="body2">No comments yet. Be the first to comment!</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {epicComments.map((comment) => (
                          <Box key={comment.id}>
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 1, backgroundColor: 'background.paper', p: 1, borderRadius: 1, borderColor: 'divider', borderWidth: 1, borderStyle: 'solid' }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                                {getInitials(comment.authorName)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" fontWeight={600}>{comment.authorName}</Typography>
                                  <Typography variant="caption" color="text.secondary">{formatDate(comment.createdAt)}</Typography>
                                  {comment.updatedAt && (
                                    <Typography variant="caption" color="text.secondary">(edited)</Typography>
                                  )}
                                </Box>

                                {editingCommentId === comment.id ? (
                                  <Box sx={{ mt: 1 }}>
                                    <RichTextEditor
                                      ref={editCommentEditorRef}
                                      extensions={commentExtensions}
                                      content={editingCommentContent}
                                      onUpdate={({ editor }) => setEditingCommentContent(editor.getHTML())}
                                      renderControls={() => (
                                        <MenuControlsContainer>
                                          <MenuButtonBold />
                                          <MenuButtonItalic />
                                          <MenuButtonUnderline />
                                          <MenuDivider />
                                          <MenuButtonBulletedList />
                                          <MenuButtonOrderedList />
                                        </MenuControlsContainer>
                                      )}
                                      RichTextFieldProps={{ variant: 'outlined' }}
                                    >
                                      {() => <></>}
                                    </RichTextEditor>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                                      <Button size="small" onClick={() => setEditingCommentId(null)}>Cancel</Button>
                                      <Button size="small" variant="contained" onClick={() => handleSaveEditEpicComment(comment.id)}>Save</Button>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{ mt: 0.5, fontSize: '0.875rem', '& p': { margin: 0 }, '& ul, & ol': { pl: 2, my: 0.5 } }}
                                    dangerouslySetInnerHTML={{ __html: comment.content }}
                                  />
                                )}

                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {REACTION_EMOJIS.map((emoji) => {
                                    const reaction = comment.reactions.find((r) => r.emoji === emoji);
                                    const count = reaction?.userIds.length ?? 0;
                                    const active = reaction?.userIds.includes(CURRENT_USER_ID) ?? false;
                                    return (
                                      <Chip
                                        key={emoji}
                                        label={count > 0 ? `${emoji} ${count}` : emoji}
                                        size="small"
                                        variant={active ? 'filled' : 'outlined'}
                                        color={active ? 'primary' : 'default'}
                                        onClick={() => handleToggleEpicReaction(comment.id, emoji)}
                                        sx={{ cursor: 'pointer', fontSize: '0.85rem', height: 24 }}
                                      />
                                    );
                                  })}
                                  <Tooltip title="Reply to this comment">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setReplyingToCommentId(replyingToCommentId === comment.id ? null : comment.id);
                                        setReplyContent('');
                                      }}
                                      sx={{ ml: 0.5 }}
                                      aria-label="Reply"
                                    >
                                      <ReplyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {comment.authorId === CURRENT_USER_ID && (
                                    <>
                                      <Tooltip title="Edit comment">
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setEditingCommentId(comment.id);
                                            setEditingCommentContent(comment.content);
                                            setTimeout(() => {
                                              editCommentEditorRef.current?.editor?.commands.setContent(comment.content);
                                            }, 0);
                                          }}
                                          aria-label="Edit comment"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete comment">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteEpicComment(comment.id)}
                                          aria-label="Delete comment"
                                          sx={{ color: 'error.main' }}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>

                                {comment.replies.length > 0 && (
                                  <Stack spacing={1} sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                                    {comment.replies.map((reply) => (
                                      <Box key={reply.id} sx={{ display: 'flex', gap: 1 }}>
                                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main', fontSize: '0.65rem', flexShrink: 0 }}>
                                          {getInitials(reply.authorName)}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="caption" fontWeight={600}>{reply.authorName}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(reply.createdAt)}</Typography>
                                            {reply.updatedAt && (
                                              <Typography variant="caption" color="text.secondary">(edited)</Typography>
                                            )}
                                          </Box>
                                          {editingReplyId === reply.id ? (
                                            <Box>
                                              <TextField
                                                size="small"
                                                fullWidth
                                                multiline
                                                maxRows={4}
                                                value={editingReplyContent}
                                                onChange={(e) => setEditingReplyContent(e.target.value)}
                                                sx={{ mt: 0.5 }}
                                              />
                                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, justifyContent: 'flex-end' }}>
                                                <Button size="small" onClick={() => setEditingReplyId(null)}>Cancel</Button>
                                                <Button size="small" variant="contained" onClick={() => handleSaveEditEpicReply(comment.id, reply.id)}>Save</Button>
                                              </Box>
                                            </Box>
                                          ) : (
                                            <Typography variant="body2" sx={{ mt: 0.25 }}>{reply.content}</Typography>
                                          )}
                                          {reply.authorId === CURRENT_USER_ID && editingReplyId !== reply.id && (
                                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                              <IconButton size="small" onClick={() => { setEditingReplyId(reply.id); setEditingReplyContent(reply.content); }} aria-label="Edit reply">
                                                <EditIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                              <IconButton size="small" onClick={() => handleDeleteEpicReply(comment.id, reply.id)} sx={{ color: 'error.main' }} aria-label="Delete reply">
                                                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>
                                    ))}
                                  </Stack>
                                )}

                                {replyingToCommentId === comment.id && (
                                  <Box sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                      Replying to <strong>{comment.authorName}</strong>
                                    </Typography>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      multiline
                                      maxRows={4}
                                      placeholder="Write a reply…"
                                      value={replyContent}
                                      onChange={(e) => setReplyContent(e.target.value)}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, justifyContent: 'flex-end' }}>
                                      <Button size="small" onClick={() => setReplyingToCommentId(null)}>Cancel</Button>
                                      <Button size="small" variant="contained" disabled={!replyContent.trim()} onClick={() => handlePostEpicReply(comment.id)}>Reply</Button>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2">Save the epic first to add comments.</Typography>
                  </Box>
                )}
              </Box>
            )
          }
        </Paper >

        {/* Edit Category Modal */}
        < Dialog open={editCatModalOpen} onClose={() => setEditCatModalOpen(false)} maxWidth="xs" fullWidth >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Edit Category</Typography>
              <IconButton size="small" onClick={() => setEditCatModalOpen(false)}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Category Name"
                value={editCatName}
                onChange={(e) => setEditCatName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveCatEdit()}
                fullWidth
                autoFocus
              />
              <Box>
                <Typography variant="body2" gutterBottom>Color</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {colorPalette.map((color) => (
                    <Box
                      key={color}
                      onClick={() => setEditCatColor(color)}
                      sx={{
                        width: 28, height: 28, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                        border: '2px solid',
                        borderColor: editCatColor === color ? 'text.primary' : 'transparent',
                        '&:hover': { transform: 'scale(1.15)' },
                        transition: 'transform 0.1s',
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: editCatColor }} />
                <Typography variant="body2" color="text.secondary">
                  {editCatName || 'Category preview'}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <ModalActionFooter
              onSave={handleSaveCatEdit}
              saveDisabled={!editCatName.trim()}
              onCancel={() => setEditCatModalOpen(false)}
              onDelete={handleConfirmDeleteCat}
              deleteLabel="Delete Category"
              deleteConfirmMessage={`Delete "${editCatName}"?`}
            />
          </DialogActions>
        </Dialog >

        {/* Add Category Modal */}
        < Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="xs" fullWidth >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Add Category</Typography>
              <IconButton size="small" onClick={() => setAddModalOpen(false)}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Category Name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                fullWidth
                autoFocus
              />
              <Box>
                <Typography variant="body2" gutterBottom>Color</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {colorPalette.map((color) => (
                    <Box
                      key={color}
                      onClick={() => setNewCategoryColor(color)}
                      sx={{
                        width: 28, height: 28, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                        border: '2px solid',
                        borderColor: newCategoryColor === color ? 'text.primary' : 'transparent',
                        '&:hover': { transform: 'scale(1.15)' },
                        transition: 'transform 0.1s',
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: newCategoryColor }} />
                <Typography variant="body2" color="text.secondary">
                  {newCategoryName || 'Category preview'}
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <ModalActionFooter
              onSave={handleAddCategory}
              saveLabel="Add"
              saveDisabled={!newCategoryName.trim()}
              onCancel={() => setAddModalOpen(false)}
            />
          </DialogActions>
        </Dialog >

        {/* Story Modal */}
        < Dialog open={storyModalOpen} onClose={() => setStoryModalOpen(false)} maxWidth="sm" fullWidth >
          <DialogTitle sx={{ position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArticleIcon fontSize="large" sx={{ color: '#2196F3' }} />
              <Typography variant="h6">{editingStory ? storyTitle : 'Add Story'}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setStoryModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Story Title"
                value={storyTitle}
                onChange={(e) => setStoryTitle(e.target.value)}
                fullWidth
                required
                autoFocus
              />
              <TextField
                label="Story Description"
                value={storyDesc}
                onChange={(e) => setStoryDesc(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={allEpics}
                  getOptionLabel={(option) => option.title}
                  value={selectedStoryEpic}
                  onChange={(_e, val) => setStoryEpicId(val ? val.id : '')}
                  renderInput={(params) => <TextField {...params} label="Epic" />}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Box>
              <FormControl>
                <FormLabel>Story Type</FormLabel>
                <RadioGroup
                  row
                  value={storyType}
                  onChange={(e) => setStoryType(e.target.value as StoryType)}
                >
                  <FormControlLabel value="Engineering" control={<Radio />} label="Engineering" />
                </RadioGroup>
              </FormControl>
              {storyType === 'Engineering' && (
                <TextField
                  select
                  label="Engineering Type"
                  value={storyEngSubtype}
                  onChange={(e) => setStoryEngSubtype(e.target.value as EngineeringSubtype)}
                  fullWidth
                >
                  {(['Frontend', 'Backend', 'Quality', 'DevOps', 'Fullstack'] as const).map((sub) => (
                    <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                  ))}
                </TextField>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 120 }}>Story Points</Typography>
                <NumberInput
                  value={storyPoints}
                  onChange={setStoryPoints}
                  min={0}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <ModalActionFooter
              onSave={handleSaveStory}
              saveLabel={editingStory ? 'Update' : 'Add Story'}
              saveDisabled={!storyTitle.trim()}
              onCancel={() => setStoryModalOpen(false)}
              onDelete={editingStory ? () => handleDeleteStory(editingStory.id) : undefined}
              deleteLabel="Delete"
              deleteConfirmMessage={editingStory ? `Delete "${editingStory.title}"?` : undefined}
            />
          </DialogActions>
        </Dialog >
      </Box >

      {/* JSON Config Viewer Modal */}
      {developerMode && existingEpic && (
        <Dialog open={jsonViewerOpen} onClose={() => setJsonViewerOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon sx={{ color: 'warning.main' }} />
                <Typography variant="h6">Epic Configuration</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={jsonCopied ? 'Copied!' : 'Copy JSON'} arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(existingEpic, null, 2));
                      setJsonCopied(true);
                      setTimeout(() => setJsonCopied(false), 2000);
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" arrow>
                  <IconButton size="small" onClick={() => setJsonViewerOpen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <JsonViewer jsonStr={JSON.stringify(existingEpic, null, 2)} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default EpicForm;
